/**
 * The PUBLIC face of a Symphony profile link — what `/me/<handle>[/<context>]` renders, and nothing more
 * (docs/symphony-profile-links-design.md §5, §7.1). Only published fields of an active link ever reach this
 * shape; the owner's private instructions, their metasite and their recipient id never do.
 *
 * The type is the contract between the page and its data adapter (`lib/profile-link-card-server.ts`), which
 * reads VMR `ProfileLinkService.GetPublicCard` — its `PublicProfileCard` message, field for field.
 */

/** The owner's three colours. STRICTLY `#rrggbb` — any other value is replaced, never interpolated (§5.2). */
export interface ProfilePalette {
  accent: string;
  bg: string;
  fg: string;
}

export interface PublicProfileCard {
  /** The vanity handle, lowercased — the `<handle>` of the URL. */
  handle: string;
  /** The context link's slug, lowercased. Absent on the main profile, and absent when `fellBackToMain`. */
  contextSlug?: string;
  /** The context link's own label ("DevConf 26"). */
  contextTitle?: string;
  /** The owner's Wix account name. Self-asserted — never styled as verified (§12.3). */
  displayName: string;
  headline: string;
  /** Plain text, rendered as text, never as HTML. */
  bio: string;
  /** "Ask me about…" chips. */
  topics: string[];
  /**
   * The owner's assistant's opening line. Optional: the public card carries it only once the VMR serves it
   * (`PublicProfileCard.greeting`); absent, the page shows nothing in its place.
   */
  greeting?: string;
  /** Tap-to-start questions ("Try asking"). Optional, like `greeting`: absent or empty renders nothing. */
  suggestedAsks?: string[];
  palette: ProfilePalette;
  /** The WhatsApp pre-fill short code (`SY-7KQ2`) that maps server-side to (link, revision) — §6.2. */
  waShortCode: string;
  /** The "Contact me on Symphony" target: `symphony.wix.app/me/<h>[/<c>]?s=true`, so it goes through jot (§4.2). */
  appLink: string;
  /**
   * The context the URL named is expired, paused, archived or unknown, so this is the MAIN profile (D7, §4.5).
   * The page then renders byte-identically to `/me/<handle>` — no "event ended" line, which would reveal
   * which slugs exist.
   */
  fellBackToMain: boolean;
}

const HEX = /^#[0-9a-f]{6}$/i;

export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX.test(value);
}

/* EXEMPT — the Symphony default palette is DATA, the same kind of value an owner's palette is: it is
   written into the page as custom properties, exactly where an owner's colours would be, so it cannot be a
   var() reference. The brand green is --ds-fill-accent-brand-default. */
export const SYMPHONY_PROFILE_PALETTE: ProfilePalette = { accent: '#a4ff70', bg: '#f7f8f5', fg: '#1d1f1b' };
/* end EXEMPT */

/** Each colour that is not `#rrggbb` takes the Symphony default; the rest are lowercased. */
export function normalizePalette(palette: Partial<ProfilePalette> | null | undefined): ProfilePalette {
  const pick = (key: keyof ProfilePalette) => {
    const value = palette?.[key];
    return isHexColor(value) ? value.toLowerCase() : SYMPHONY_PROFILE_PALETTE[key];
  };
  return { accent: pick('accent'), bg: pick('bg'), fg: pick('fg') };
}
